import './fonts';
import './bootstrap';
import '../../css/app.css';
import '@xyflow/react/dist/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';

import NProgress from 'nprogress';
import Cookies from 'universal-cookie';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createInertiaApp, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ToastContainer } from 'react-toastify';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { getPosthog } from './util/posthog.util';

// Providers
import { ApplicationProvider } from './provider/Application';
import { AppModalProvider } from './provider/AppModal';
import { BottomSheetProvider } from './provider/BottomSheet';

// Redux
import { store } from './store';

// Analytics
import analytics from './analytics';
import { clarity } from 'react-microsoft-clarity';

Sentry.init({
    dsn: 'https://fbc84dcea9876bbb9fe4c5df16563034@o4507311208136704.ingest.de.sentry.io/4508993522368592',
    enabled: import.meta.env.PROD,
    debug: import.meta.env.DEV,
    environment: import.meta.env.DEV ? 'development' : 'production',
});

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
    title: (title) => {
        if (
            title.startsWith('ReelFlow Dashboard') ||
            title.startsWith('ReelFlow Login') ||
            title.startsWith('ReelFlow Forgot')
        ) {
            return title;
        }

        return `${title} – ${appName}`;
    },
    resolve: (name) => {
        return resolvePageComponent(
            `./Pages/${name}.tsx`,
            import.meta.glob('./Pages/**/*.tsx'),
        );
    },
    setup({ el, App, props }) {
        const content = (
            <Provider store={store}>
                <ApplicationProvider>
                    <AppModalProvider>
                        <BottomSheetProvider>
                            <SentryErrorBoundary fallback={undefined}>
                                <App {...props} />
                                <ToastContainer />
                            </SentryErrorBoundary>
                        </BottomSheetProvider>
                    </AppModalProvider>
                </ApplicationProvider>
            </Provider>
        );

        if (import.meta.env.DEV) {
            createRoot(el).render(content);

            return;
        }

        hydrateRoot(el, content);
    },
    // NOTE: Hide the default progress bar since we implement custom one
    progress: false,
});

NProgress.configure({
    template:
        '<div class="bar !bg-brand-ocean !opacity-100" role="bar" style="height:3px"><div class="peg"></div></div>',
});

router.on('start', (event) => {
    NProgress.start();
});

router.on('finish', (event) => {
    NProgress.done();
});

router.on('success', (event) => {
    const cookieMap = new Cookies(document.cookie);

    cookieMap.set('reelflow_ref_count', '1', { path: '/' });

    // @ts-ignore
    if (event.detail.page) {
        // @ts-expect-error
        const user = event.detail.page.props?.auth?.user;

        if (!user) {
            return;
        }

        const isTrackingEnabled = user.user_tracking_enabled;
        const posthog = getPosthog();

        if (posthog) {
            const userId = String(user.id);
            const hasOptedIn = posthog.has_opted_in_capturing?.();
            const hasOptedOut = posthog.has_opted_out_capturing?.();

            if (isTrackingEnabled) {
                if (!hasOptedIn) {
                    posthog.opt_in_capturing();
                }

                // Avoid noisy re-identify calls on every navigation.
                if (posthog.get_distinct_id?.() !== userId) {
                    posthog.identify(userId, {
                        email: user.email,
                        name: user.name,
                        workspace_id: user.current_team_id,
                    });
                }
            } else if (!hasOptedOut) {
                posthog.opt_out_capturing();
            }
        }

        if (!clarity.hasStarted() && process.env.NODE_ENV === 'production') {
            analytics.page();

            clarity.init(import.meta.env.VITE_MICROSOFT_CLARITY_ID);
            window.clarity?.('consentv2', {
                ad_Storage: 'denied',
                analytics_Storage: isTrackingEnabled ? 'granted' : 'denied',
            });

            const isStaging = window?.location?.origin?.includes('staging');
            const userEmail = user.email;

            const userType = !userEmail
                ? 'unknown'
                : userEmail.includes('@reelflow')
                  ? 'reelflow_internal'
                  : 'not_reelflow_internal';

            clarity.setTag('env', isStaging ? 'staging' : 'production');
            clarity.setTag('user_type', userType);
        }
    }
});

router.on('navigate', () => {
    const cookieMap = new Cookies(document.cookie);

    cookieMap.set('reelflow_ref_count', '1', { path: '/' });
});
